import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { ListFormBrandType } from './listTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.list.style, {
  labels: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.list.schema.backgroundImage'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  indent: {
    marginRight: genBrandField({
      as: FieldNumber,
      label: t('input.label.iconIndents'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 64 },
    }),
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('input.label.lineGap'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 64 },
    }),
  },
  animation: {
    fade: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.list.schema.fade'),
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.list.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const ListStyleForm: ListFormBrandType = ({ device, subConfigs, edited, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.list.tags.labels')}</Typography>
        <FormField config={schema.labels.backgroundImage} />
        <FormField config={schema.labels.backgroundColor} />
        <FormField config={schema.labels.borderStyle} />
        <FormField config={schema.labels.borderColor} />
        <FormField config={schema.labels.borderWidth} />

        <Typography weight='medium'>{t('elements.list.tags.indent')}</Typography>
        <FormField config={schema.indent.marginRight} />
        <FormField config={schema.indent.marginTop} />

        <Typography weight='medium'>{t('elements.list.tags.animation')}</Typography>
        <FormField config={schema.animation.fade} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default ListStyleForm
